import { useQuery } from '@tanstack/react-query';
import { Tooltip } from '@vd/ui';
import { Puzzle } from 'lucide-react';
import { api } from '../../lib/api';
import { useGuardedNavigate } from '../../app/NavigationGuard';
import styles from './TopBar.module.css';

/**
 * "Get the Extension" chip rendered in the TopBar's right section.
 *
 * Stays visible until the current user has at least one active extension
 * OAuth token (the only authoritative signal that they've installed AND
 * authenticated the extension at least once). Existing team members who
 * are new to the system see this just like brand-new users — the company
 * having tickets doesn't mean THIS user has the extension. Once the user
 * installs + connects, the next render hides the chip.
 *
 * Refetches when the window regains focus so navigating to
 * /me/settings/extension and back picks up the new state without a hard
 * reload.
 */
export function GetExtensionPrompt() {
  const navigate = useGuardedNavigate();
  const { data } = useQuery({
    queryKey: ['extension-status'],
    queryFn: () => api.getExtensionStatus(),
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });

  if (!data || data.installed) return null;

  return (
    <Tooltip content="Install the Toado Chrome extension to capture bugs from any page" placement="bottom">
      <button
        type="button"
        onClick={() => navigate('/me/settings/extension')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          border: '1px solid var(--accent)',
          background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
          color: 'var(--accent)',
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
        aria-label="Get the Toado extension"
      >
        <Puzzle size={12} />
        <span className={styles.wideOnly}>Get the extension</span>
      </button>
    </Tooltip>
  );
}
